import React from "react";
import { Box, Paper, Skeleton, useMediaQuery } from "@mui/material";
import PageContainer from "../PageContainer";
import PageSection from "../PageSection";

const TeamManagementSkeleton: React.FC = () => {
  const isMdUp = useMediaQuery("(min-width:900px)");

  return (
    <PageContainer sx={{ mt: 2 }}>
      <PageSection sx={{ mb: 2 }}>
        {/* Stats Overview Skeleton */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              boxShadow: "none",
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: { xs: 3, md: 5 },
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {/* Total Members */}
              <Box sx={{ textAlign: "center" }}>
                <Skeleton
                  variant="text"
                  width={80}
                  height={60}
                  sx={{ mx: "auto", mb: 0.5 }}
                />
                <Skeleton variant="text" width={120} height={24} />
              </Box>

              {/* Divider */}
              <Skeleton
                variant="rectangular"
                width={2}
                height={60}
                sx={{ borderRadius: 1 }}
              />

              {/* Admin Members */}
              <Box sx={{ textAlign: "center" }}>
                <Skeleton
                  variant="text"
                  width={80}
                  height={60}
                  sx={{ mx: "auto", mb: 0.5 }}
                />
                <Skeleton variant="text" width={130} height={24} />
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Search Section Skeleton */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              height: 48,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              px: 2,
              gap: 1,
            }}
          >
            {/* Search icon skeleton */}
            <Skeleton variant="circular" width={20} height={20} />
            {/* Search text skeleton */}
            <Skeleton variant="text" width="30%" height={20} />
          </Box>
        </Box>

        {/* Team List Skeleton */}
        {isMdUp ? (
          // Desktop Layout Skeleton
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {Array.from({ length: 5 }).map((_, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 2.5,
                  minHeight: 80,
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "2fr 2fr 1.5fr 2fr 1.5fr 0.8fr",
                    gap: 2,
                    alignItems: "center",
                  }}
                >
                  {/* Name Column with Avatar */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Skeleton
                      variant="circular"
                      width={40}
                      height={40}
                      sx={{ flexShrink: 0 }}
                    />
                    <Skeleton variant="text" width="70%" height={20} />
                  </Box>

                  {/* Username Column */}
                  <Skeleton variant="text" width="80%" height={20} />

                  {/* Phone Column */}
                  <Skeleton variant="text" width="60%" height={20} />

                  {/* Circles Column */}
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Skeleton variant="rounded" width={60} height={20} />
                    <Skeleton variant="rounded" width={50} height={20} />
                  </Box>

                  {/* Date Column */}
                  <Skeleton variant="text" width="70%" height={20} />

                  {/* Actions Column */}
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Skeleton variant="circular" width={32} height={32} />
                    <Skeleton variant="circular" width={32} height={32} />
                    <Skeleton variant="circular" width={32} height={32} />
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        ) : (
          // Mobile Card Skeleton
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {Array.from({ length: 3 }).map((_, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  p: 2.5,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  minHeight: 80,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2,
                  }}
                >
                  {/* Avatar */}
                  <Skeleton
                    variant="circular"
                    width={40}
                    height={40}
                    sx={{ flexShrink: 0 }}
                  />

                  {/* Content */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Name */}
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={24}
                      sx={{ mb: 0.5 }}
                    />

                    {/* Username */}
                    <Skeleton
                      variant="text"
                      width="80%"
                      height={18}
                      sx={{ mb: 0.5 }}
                    />

                    {/* Phone */}
                    <Skeleton
                      variant="text"
                      width="50%"
                      height={18}
                      sx={{ mb: 1 }}
                    />

                    {/* Circles */}
                    <Box sx={{ display: "flex", gap: 0.5, mb: 1 }}>
                      <Skeleton variant="rounded" width={60} height={18} />
                      <Skeleton variant="rounded" width={45} height={18} />
                    </Box>

                    {/* Actions and Date */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Skeleton variant="circular" width={28} height={28} />
                        <Skeleton variant="circular" width={28} height={28} />
                        <Skeleton variant="circular" width={28} height={28} />
                      </Box>
                      <Skeleton variant="text" width={70} height={16} />
                    </Box>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </PageSection>
    </PageContainer>
  );
};

export default TeamManagementSkeleton;
